/**
 * AnnotationPanel - Reusable panel wrapper for independent annotation types
 *
 * Provides a collapsible panel with consistent form fields (label, notes, importance)
 * and save functionality. Each annotation type (Body Region, Distance Goal, Movement Goal)
 * wraps its specific UI in this panel for a unified, independent annotation experience.
 *
 * Design: Industrial-Athletic - precise, engineered, professional coaching tool aesthetic
 */

import { useState, useEffect, useCallback } from 'react';
import { ChevronDownIcon, ChevronUpIcon, CheckIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { cn } from '@/utils/cn';
import { Spinner } from '@/components/ui';
import { TIMEOUTS, SNAP_CONFIG } from '@/utils/constants';

const ACCENT_COLORS = {
  blue: {
    border: 'border-blue-500',
    borderLight: 'border-blue-500/30',
    bg: 'bg-blue-500',
    bgLight: 'bg-blue-500/15',
    bgGradient: 'from-blue-500 to-blue-600',
    text: 'text-blue-400',
    textLight: 'text-blue-400',
    ring: 'ring-blue-500/20',
    sliderTrack: 'bg-gradient-to-r from-blue-800 via-blue-500 to-blue-400',
    hover: 'hover:bg-blue-600',
    headerBg: 'bg-dark-elevated-2',
  },
  purple: {
    border: 'border-violet-500',
    borderLight: 'border-violet-500/30',
    bg: 'bg-violet-500',
    bgLight: 'bg-violet-500/15',
    bgGradient: 'from-violet-500 to-violet-600',
    text: 'text-violet-400',
    textLight: 'text-violet-400',
    ring: 'ring-violet-500/20',
    sliderTrack: 'bg-gradient-to-r from-violet-800 via-violet-500 to-violet-400',
    hover: 'hover:bg-violet-600',
    headerBg: 'bg-dark-elevated-2',
  },
  green: {
    border: 'border-emerald-500',
    borderLight: 'border-emerald-500/30',
    bg: 'bg-emerald-500',
    bgLight: 'bg-emerald-500/15',
    bgGradient: 'from-emerald-500 to-emerald-600',
    text: 'text-emerald-400',
    textLight: 'text-emerald-400',
    ring: 'ring-emerald-500/20',
    sliderTrack: 'bg-gradient-to-r from-emerald-800 via-emerald-500 to-emerald-400',
    hover: 'hover:bg-emerald-600',
    headerBg: 'bg-dark-elevated-2',
  },
  amber: {
    border: 'border-amber-500',
    borderLight: 'border-amber-500/30',
    bg: 'bg-amber-500',
    bgLight: 'bg-amber-500/15',
    bgGradient: 'from-amber-500 to-amber-600',
    text: 'text-amber-400',
    textLight: 'text-amber-400',
    ring: 'ring-amber-500/20',
    sliderTrack: 'bg-gradient-to-r from-amber-800 via-amber-500 to-amber-400',
    hover: 'hover:bg-amber-600',
    headerBg: 'bg-dark-elevated-2',
  },
} as const;

const IMPORTANCE_LABELS = [
  { value: 0, label: 'Low', position: '0%' },
  { value: 33, label: 'Medium', position: '33%' },
  { value: 66, label: 'High', position: '66%' },
  { value: 100, label: 'Critical', position: '100%' },
];

interface AnnotationPanelProps {
  // Panel identity
  title: string;
  subtitle?: string;
  icon: React.ReactNode;
  accentColor: 'blue' | 'purple' | 'green' | 'amber';
  panelNumber: number;
  totalPanels: number;

  // Expansion state
  isExpanded: boolean;
  onToggle: () => void;

  // Form fields
  label: string;
  onLabelChange: (label: string) => void;
  notes: string;
  onNotesChange: (notes: string) => void;
  importance: number;
  onImportanceChange: (importance: number) => void;

  // Save state
  canSave: boolean;
  isSaving: boolean;
  isSaved: boolean;
  onSave: () => void;
  saveButtonText?: string;

  // Content
  children: React.ReactNode;

  // Optional className
  className?: string;
}

export default function AnnotationPanel({
  title,
  subtitle,
  icon,
  accentColor,
  panelNumber,
  totalPanels,
  isExpanded,
  onToggle,
  label,
  onLabelChange,
  notes,
  onNotesChange,
  importance,
  onImportanceChange,
  canSave,
  isSaving,
  isSaved,
  onSave,
  saveButtonText = 'Save Annotation',
  children,
  className,
}: AnnotationPanelProps) {
  const colors = ACCENT_COLORS[accentColor];
  const [showSavedPulse, setShowSavedPulse] = useState(false);

  // Show pulse animation when isSaved becomes true
  useEffect(() => {
    if (isSaved) {
      setShowSavedPulse(true);
      const timer = setTimeout(() => setShowSavedPulse(false), TIMEOUTS.SAVE_PULSE_ANIMATION);
      return () => clearTimeout(timer);
    }
  }, [isSaved]);

  // Get importance label based on current value
  const getImportanceLabel = useCallback((value: number) => {
    if (value >= 85) return 'Critical';
    if (value >= 55) return 'High';
    if (value >= 25) return 'Medium';
    return 'Low';
  }, []);

  // Snap importance to nearest marker
  const handleImportanceChange = useCallback((value: number) => {
    // Snap to nearest marker if within threshold
    const markers = [0, 33, 66, 100];

    for (const marker of markers) {
      if (Math.abs(value - marker) <= SNAP_CONFIG.FRAME_COUNT) {
        onImportanceChange(marker);
        return;
      }
    }
    onImportanceChange(value);
  }, [onImportanceChange]);

  return (
    <div
      className={cn(
        'rounded-xl overflow-hidden transition-all duration-300 ease-out',
        'border-2 shadow-sm',
        isExpanded ? colors.border : 'border-border-subtle',
        isExpanded ? 'shadow-lg' : 'hover:shadow-md hover:border-border-strong',
        className
      )}
      style={{
        // Subtle left accent stripe
        borderLeftWidth: isExpanded ? '4px' : '2px',
      }}
    >
      {/* Header */}
      <button
        type="button"
        onClick={onToggle}
        className={cn(
          'w-full flex items-center justify-between px-4 py-3.5',
          'transition-all duration-200 ease-out',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
          colors.ring,
          isExpanded ? colors.headerBg : 'bg-dark-elevated-1 hover:bg-dark-elevated-2'
        )}
      >
        <div className="flex items-center gap-3">
          {/* Icon container */}
          <div
            className={cn(
              'flex items-center justify-center w-9 h-9 rounded-lg transition-all duration-200',
              isExpanded ? `${colors.bg} text-white shadow-md` : 'bg-dark-elevated-2 text-text-secondary'
            )}
          >
            {icon}
          </div>

          {/* Title & Subtitle */}
          <div className="text-left">
            <h3
              className={cn(
                'text-sm font-semibold tracking-tight transition-colors duration-200',
                isExpanded ? colors.text : 'text-text-primary'
              )}
            >
              {title}
            </h3>
            {subtitle && (
              <p className="text-xs text-text-secondary mt-0.5 font-medium">
                {subtitle}
              </p>
            )}
          </div>
        </div>

        {/* Right side: panel indicator, saved status, chevron */}
        <div className="flex items-center gap-3">
          {/* Panel indicator */}
          <span
            className={cn(
              'text-xs font-mono font-medium px-2 py-0.5 rounded-full',
              'bg-dark-elevated-2 text-text-secondary'
            )}
          >
            {panelNumber}/{totalPanels}
          </span>

          {/* Saved indicator */}
          {isSaved && (
            <div
              className={cn(
                'flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full',
                colors.bgLight,
                colors.text,
                showSavedPulse && 'animate-pulse'
              )}
            >
              <CheckCircleIcon className="w-4 h-4" />
              <span>Saved</span>
            </div>
          )}

          {/* Chevron */}
          <div
            className={cn(
              'p-1 rounded-full transition-all duration-200',
              isExpanded ? colors.bgLight : 'bg-dark-elevated-2'
            )}
          >
            {isExpanded ? (
              <ChevronUpIcon className={cn('w-4 h-4', colors.text)} />
            ) : (
              <ChevronDownIcon className="w-4 h-4 text-text-secondary" />
            )}
          </div>
        </div>
      </button>

      {/* Expandable Content */}
      <div
        className={cn(
          'overflow-hidden transition-all duration-300 ease-out',
          isExpanded ? 'max-h-[4000px] opacity-100' : 'max-h-0 opacity-0'
        )}
      >
        <div className="bg-dark-card">
          {/* Custom content area */}
          <div className="px-4 pt-4 pb-3">
            {children}
          </div>

          {/* Divider */}
          <div className="mx-4">
            <div className="h-px bg-gradient-to-r from-transparent via-border-subtle to-transparent" />
          </div>

          {/* Form fields */}
          <div className="px-4 py-4 space-y-4">
            {/* Label Input */}
            <div className="space-y-1.5">
              <label
                htmlFor={`panel-${panelNumber}-label`}
                className="block text-xs font-semibold text-text-secondary uppercase tracking-wider"
              >
                Label
              </label>
              <input
                id={`panel-${panelNumber}-label`}
                type="text"
                value={label}
                onChange={(e) => onLabelChange(e.target.value)}
                placeholder="Enter a descriptive label..."
                className={cn(
                  'w-full px-3 py-2.5 rounded-lg text-sm text-text-primary',
                  'border-2 border-border-subtle bg-dark-elevated-1',
                  'placeholder:text-text-muted',
                  'transition-all duration-200',
                  'focus:outline-none focus:border-border-strong focus:bg-dark-elevated-2 focus:ring-4',
                  colors.ring
                )}
              />
            </div>

            {/* Notes Textarea */}
            <div className="space-y-1.5">
              <label
                htmlFor={`panel-${panelNumber}-notes`}
                className="block text-xs font-semibold text-text-secondary uppercase tracking-wider"
              >
                Notes <span className="text-text-muted font-normal normal-case">(optional)</span>
              </label>
              <textarea
                id={`panel-${panelNumber}-notes`}
                value={notes}
                onChange={(e) => onNotesChange(e.target.value)}
                placeholder="Add coaching cues or guidance for athletes..."
                rows={2}
                className={cn(
                  'w-full px-3 py-2.5 rounded-lg text-sm resize-y min-h-[60px] text-text-primary',
                  'border-2 border-border-subtle bg-dark-elevated-1',
                  'placeholder:text-text-muted',
                  'transition-all duration-200',
                  'focus:outline-none focus:border-border-strong focus:bg-dark-elevated-2 focus:ring-4',
                  colors.ring
                )}
              />
            </div>

            {/* Importance Slider */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="block text-xs font-semibold text-text-secondary uppercase tracking-wider">
                  Importance
                </label>
                <span
                  className={cn(
                    'text-xs font-bold px-2 py-0.5 rounded-full',
                    importance >= 85 ? 'bg-danger/20 text-danger' :
                    importance >= 55 ? 'bg-warning/20 text-warning' :
                    importance >= 25 ? 'bg-amber-500/20 text-amber-400' :
                    'bg-dark-elevated-2 text-text-secondary'
                  )}
                >
                  {getImportanceLabel(importance)}
                </span>
              </div>

              {/* Custom slider */}
              <div className="relative pt-1 pb-6">
                {/* Track background */}
                <div className="absolute inset-x-0 top-3 h-2 rounded-full bg-dark-elevated-2" />

                {/* Filled track */}
                <div
                  className={cn('absolute top-3 left-0 h-2 rounded-full', colors.sliderTrack)}
                  style={{ width: `${importance}%` }}
                />

                {/* Markers */}
                <div className="absolute inset-x-0 top-2.5 flex justify-between pointer-events-none">
                  {IMPORTANCE_LABELS.map((marker) => (
                    <div
                      key={marker.value}
                      className={cn(
                        'w-3 h-3 rounded-full border-2 bg-dark-card transition-all duration-200',
                        importance >= marker.value ? colors.border : 'border-border-subtle'
                      )}
                    />
                  ))}
                </div>

                {/* Range input */}
                <input
                  type="range"
                  min="0"
                  max="100"
                  step="1"
                  value={importance}
                  onChange={(e) => handleImportanceChange(parseInt(e.target.value))}
                  className={cn(
                    'relative w-full h-2 appearance-none cursor-pointer',
                    'bg-transparent z-10',
                    '[&::-webkit-slider-thumb]:appearance-none',
                    '[&::-webkit-slider-thumb]:w-5',
                    '[&::-webkit-slider-thumb]:h-5',
                    '[&::-webkit-slider-thumb]:rounded-full',
                    '[&::-webkit-slider-thumb]:bg-white',
                    '[&::-webkit-slider-thumb]:border-4',
                    '[&::-webkit-slider-thumb]:shadow-lg',
                    '[&::-webkit-slider-thumb]:cursor-grab',
                    '[&::-webkit-slider-thumb]:active:cursor-grabbing',
                    '[&::-webkit-slider-thumb]:transition-transform',
                    '[&::-webkit-slider-thumb]:hover:scale-110',
                    accentColor === 'blue' && '[&::-webkit-slider-thumb]:border-blue-500',
                    accentColor === 'purple' && '[&::-webkit-slider-thumb]:border-violet-500',
                    accentColor === 'green' && '[&::-webkit-slider-thumb]:border-emerald-500',
                    // Firefox
                    '[&::-moz-range-thumb]:appearance-none',
                    '[&::-moz-range-thumb]:w-5',
                    '[&::-moz-range-thumb]:h-5',
                    '[&::-moz-range-thumb]:rounded-full',
                    '[&::-moz-range-thumb]:bg-white',
                    '[&::-moz-range-thumb]:border-4',
                    '[&::-moz-range-thumb]:shadow-lg',
                    '[&::-moz-range-thumb]:cursor-grab',
                    accentColor === 'blue' && '[&::-moz-range-thumb]:border-blue-500',
                    accentColor === 'purple' && '[&::-moz-range-thumb]:border-violet-500',
                    accentColor === 'green' && '[&::-moz-range-thumb]:border-emerald-500',
                  )}
                />

                {/* Labels */}
                <div className="absolute inset-x-0 top-8 flex justify-between text-[10px] font-medium text-text-muted">
                  {IMPORTANCE_LABELS.map((marker) => (
                    <span
                      key={marker.value}
                      className={cn(
                        'transition-colors duration-200',
                        importance >= marker.value - 10 && importance <= marker.value + 10
                          ? 'text-text-primary font-semibold'
                          : ''
                      )}
                    >
                      {marker.label}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Save Button */}
          <div className="px-4 pb-4">
            <button
              type="button"
              onClick={onSave}
              disabled={!canSave || isSaving}
              className={cn(
                'w-full flex items-center justify-center gap-2 py-3 px-4 rounded-lg',
                'text-sm font-semibold text-white',
                'transition-all duration-200 ease-out',
                'focus:outline-none focus-visible:ring-4',
                colors.ring,
                canSave && !isSaving
                  ? cn(
                      `bg-gradient-to-r ${colors.bgGradient}`,
                      colors.hover,
                      'shadow-md hover:shadow-lg',
                      'active:scale-[0.98] active:shadow-sm'
                    )
                  : 'bg-dark-elevated-2 text-text-muted cursor-not-allowed'
              )}
            >
              {isSaving ? (
                <>
                  <Spinner className="w-4 h-4" />
                  <span>Saving...</span>
                </>
              ) : isSaved ? (
                <>
                  <CheckIcon className="w-4 h-4" />
                  <span>Saved!</span>
                </>
              ) : (
                <span>{saveButtonText}</span>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
